
import { useState, useEffect } from "react";
import { Group, Product } from "@/lib/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "lucide-react";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import { cn } from "@/lib/utils";

interface GroupUsageFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  group: Group;
  products: Product[];
  onRecordUsage: (groupId: string, quantities: Record<string, number>, date: Date) => void;
  defaultQuantity?: number;
  savedQuantities?: Record<string, number>;
}

const GroupUsageForm = ({
  open,
  onOpenChange,
  group,
  products,
  onRecordUsage,
  defaultQuantity = 1,
  savedQuantities = {}
}: GroupUsageFormProps) => {
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  
  // Reset form when group changes or when defaultQuantity changes
  useEffect(() => {
    if (group) {
      const groupProducts = products.filter(p => group.productIds.includes(p.id));
      const productIds = groupProducts.map(p => p.id);
      
      // Initialize with all products selected and use saved quantities or default
      setSelectedProducts(productIds);
      
      const initialQuantities: Record<string, number> = {};
      productIds.forEach(id => {
        // Use saved quantity if available, otherwise use defaultQuantity
        initialQuantities[id] = savedQuantities[id] || defaultQuantity;
      });
      
      setQuantities(initialQuantities);
      setSelectedDate(new Date()); // Reset date to today
    }
  }, [group, products, defaultQuantity, savedQuantities, open]);
  
  const handleQuantityChange = (productId: string, quantity: number) => {
    setQuantities(prev => ({
      ...prev,
      [productId]: quantity
    }));
  };
  
  const handleToggleProduct = (productId: string, checked: boolean) => {
    if (checked) {
      setSelectedProducts(prev => [...prev, productId]);
    } else {
      setSelectedProducts(prev => prev.filter(id => id !== productId));
    }
  };
  
  const handleSubmit = () => {
    // Create a record of quantities for selected products
    const selectedQuantities: Record<string, number> = {};
    
    selectedProducts.forEach(productId => {
      selectedQuantities[productId] = quantities[productId] || defaultQuantity;
    });
    
    onRecordUsage(group.id, selectedQuantities, selectedDate);
    onOpenChange(false);
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px] glass-card animate-fade-in">
        <DialogHeader>
          <DialogTitle>Record Usage for {group.name}</DialogTitle>
          <DialogDescription>
            Specify quantities for each product in this group.
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4">
          {/* Date Picker */}
          <div className="mb-4">
            <Label className="mb-2 block">Date Used</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full justify-start text-left font-normal"
                >
                  <Calendar className="mr-2 h-4 w-4" />
                  {selectedDate ? format(selectedDate, "PPP") : <span>Pick a date</span>}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <CalendarComponent
                  mode="single"
                  selected={selectedDate}
                  onSelect={(date) => date && setSelectedDate(date)}
                  initialFocus
                  className={cn("p-3 pointer-events-auto")}
                />
              </PopoverContent>
            </Popover>
          </div>
          
          <div className="max-h-[300px] overflow-y-auto space-y-4">
            {products
              .filter(product => group.productIds.includes(product.id))
              .map(product => (
                <div key={product.id} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id={`product-${product.id}`}
                        checked={selectedProducts.includes(product.id)}
                        onCheckedChange={(checked) => 
                          handleToggleProduct(product.id, checked === true)
                        }
                      />
                      <Label
                        htmlFor={`product-${product.id}`}
                        className="text-sm cursor-pointer"
                      >
                        {product.name}
                      </Label>
                    </div>
                    {selectedProducts.includes(product.id) && (
                      <div className="flex items-center space-x-2">
                        <Input
                          type="number"
                          min="1"
                          value={quantities[product.id] || defaultQuantity}
                          onChange={(e) => handleQuantityChange(
                            product.id, 
                            parseInt(e.target.value) || defaultQuantity
                          )}
                          className="w-16 h-8"
                          onClick={(e) => (e.target as HTMLInputElement).select()}
                          inputMode="numeric"
                          pattern="[0-9]*"
                        />
                        <span className="text-sm">items</span>
                      </div>
                    )}
                  </div>
                  {selectedProducts.includes(product.id) && (
                    <div className="text-xs text-gray-500 dark:text-gray-400 pl-6">
                      Current stock: {product.currentStock} units
                    </div>
                  )}
                </div>
              ))}
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit}
            disabled={selectedProducts.length === 0}
            className="btn-primary"
          >
            Record Usage
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default GroupUsageForm;
